import { getBalanceSheet, getProfitAndLoss } from "../../apiClient";
import { formatDateForAPI } from "../dateUtils";
import { sumBalances, sumByCategory } from "../collectionUtils";
import { pickDimensionFilters, pickDimensionQueryParams } from "../dimensionParams";
import { formatReportApiError, isRequestCancelled } from "../errorUtils";
import { calculateCurrentRatio, calculateWorkingCapital } from "../liquidityMetrics";
import { formatBalanceSheetData } from "../formatters/balanceSheet";
import { getMockReportData } from "../mockData";
import { calculateGrossMargin, calculateOperatingMargin } from "../profitabilityMetrics";
import { formatVerticalIncomeData } from "./profitAndLoss";

const formatActivityData = (balanceData, pnlData) => {
  const totalAssets = sumBalances(balanceData.assets);
  const totalRevenue = sumByCategory(pnlData.revenue || []);
  const totalExpenses = sumByCategory(pnlData.expenses || []);

  return {
    summary: {
      totalAssets,
      totalRevenue,
      totalExpenses,
      assetTurnover: totalAssets > 0 ? totalRevenue / totalAssets : 0,
      expenseActivity: totalRevenue > 0 ? totalExpenses / totalRevenue : 0,
    },
    activityMetrics: {
      assetUtilization: totalAssets > 0 ? (totalRevenue / totalAssets) * 100 : 0,
      expenseEfficiency: totalRevenue > 0 ? (totalExpenses / totalRevenue) * 100 : 0,
      workingCapital: calculateWorkingCapital(balanceData),
      currentRatio: calculateCurrentRatio(balanceData),
    },
    combinedData: {
      balanceSheet: formatBalanceSheetData(balanceData),
      profitAndLoss: formatVerticalIncomeData(pnlData),
    },
  };
};

const formatProfitabilityData = (balanceData, pnlData) => {
  const totalAssets = sumBalances(balanceData.assets);
  const totalEquity = sumBalances(balanceData.equity);
  const revenue = sumByCategory(pnlData.revenue || []);
  const expenses = sumByCategory(pnlData.expenses || []);
  const netIncome = revenue - expenses;

  return {
    summary: { revenue, expenses, netIncome, totalAssets, totalEquity },
    profitabilityRatios: {
      returnOnAssets: totalAssets > 0 ? (netIncome / totalAssets) * 100 : 0,
      returnOnEquity: totalEquity > 0 ? (netIncome / totalEquity) * 100 : 0,
      netProfitMargin: revenue > 0 ? (netIncome / revenue) * 100 : 0,
      grossProfitMargin: calculateGrossMargin(pnlData),
      operatingMargin: calculateOperatingMargin(pnlData),
    },
    combinedData: {
      balanceSheet: formatBalanceSheetData(balanceData),
      profitAndLoss: formatVerticalIncomeData(pnlData),
    },
  };
};

export const handleActivityProfitability = async (
  filters,
  { balanceDate, rangeEndDate, rangeStartDate },
  reportType,
  language = "ar",
  options = {}
) => {
  try {
    const [balanceResponse, pnlResponse] = await Promise.all([
      getBalanceSheet({
        date: balanceDate,
        currency: filters.currency,
        period_count: filters.period_count || 0,
        ...pickDimensionQueryParams(filters),
      }, { signal: options.signal }),
      getProfitAndLoss({
        currency: filters.currency,
        date_after: formatDateForAPI(rangeStartDate),
        date_before: formatDateForAPI(rangeEndDate),
        compare_period_unit: filters.compare_period_unit || "month",
        ...pickDimensionFilters(filters),
      }, { signal: options.signal }),
    ]);

    const data =
      reportType === "activity" ||
      reportType === "inventory" ||
      reportType === "dupont" ||
      reportType === "earningsQuality"
        ? formatActivityData(balanceResponse.data, pnlResponse.data)
        : formatProfitabilityData(balanceResponse.data, pnlResponse.data);

    return {
      success: true,
      reportType,
      isRealData: true,
      originalData: {
        balanceSheet: balanceResponse.data,
        profitAndLoss: pnlResponse.data,
      },
      data,
    };
  } catch (error) {
    if (isRequestCancelled(error)) throw error;
    console.error(`${reportType} API Error:`, error);
    return {
      success: false,
      reportType,
      isRealData: false,
      data: getMockReportData(reportType, filters),
      error: formatReportApiError(error, language),
    };
  }
};
